// UnitGroupMgr + UnitGroup + UnitGroupActions — scripted unit squads: a group declares
// its side, unit list (ut,count pairs), aggressiveness, spawn mode (spawn-in-area vs
// build-through-the-side's-builders via BuildMgr), and an action list (wait / set
// switch / move / attack / guard / guard-vicinity / mine) that executes with the same
// blocking-resume model as triggers. Activation comes from CreateAtLevelLoad, the
// CreateUnitGroup trigger action, or ActivateRandomUnitGroup (shuffle semantics — no
// repeats until all random groups have run). game/UnitGroupMgr.cpp (all 1,445 lines).

import type { SimContext } from './context.ts';
import { IniReader } from './data/ini.ts';
import {
  ParseNumber,
  ParseArea,
  ParseUnitMask,
  GetSideMaskFromCaSideMask,
} from './trigger/triggermgr.ts';
import { RadiusFromUnitCount } from './trigger/triggeractions.ts';
import type { Player } from './player/player.ts';
import { kfPlrComputer } from './player/side.ts';
import { GetSideMask } from './types.ts';
import { kumStructures } from './player/masks.ts';
import { CreateGob } from './gob/factory.ts';
import { FindNearestFreeTile } from './gob/tilesearch.ts';
import { kbfStructure, kbfMobileUnit } from './terrain/terrainmap.ts';
import { kfGobMobileUnit, kfGobUnit, kfGobActive } from './gob/gob.ts';
import { knLimitStruct, knLimitMobileUnit } from './gob/gobmgr.ts';
import type { UnitGob } from './gob/unitgob.ts';
import {
  MobileUnitGob,
  SendMoveAction,
  AggBitsFromAgg,
  kfMuntAggressivenessBits,
  kfMuntAttackEnemiesWhenMoving,
  kwxInvalid,
} from './gob/mobileunit.ts';
import type { MobileUnitConsts, StructConsts, UnitConsts } from './gob/unitconsts.ts';
import { kutNone } from './gob/builder.ts';
import { kutGalaxMiner } from './player/side.ts';
import { kgtGalaxMiner } from './gob/gobtypes.ts';
import {
  newMessage,
  kmidAttackAction,
  kmidGuardAction,
  kmidGuardVicinityAction,
  kmidMineCommand,
  kgidNull,
  ksmidNull,
} from './sm/message.ts';
import { WcFromTc } from './math/coords.ts';

// game/ht.h:5098-5107
export const kfUgNeedsUnit = 0x0001;
export const kfUgActive = 0x0002;
export const kfUgLoopForever = 0x0004;
export const kfUgCreateAtLevelLoad = 0x0008;
export const kfUgRandomGroup = 0x0010;
export const kfUgReplaceGroup = 0x0020;
export const kfUgSpawn = 0x0040;
export const kfUgWaitingToReplace = 0x0080;
export const kfUgActivatedBefore = 0x0100;
export const kfUgNotRecentlyActivated = 0x0200;

// game/ht.h:5093-5094
export const kfUleBuilt = 0x01;
export const kfUleReplicant = 0x02;

// game/res.h:735-741 — UnitGroupActions
export const knWaitUnitGroupAction = 0;
export const knSetSwitchUnitGroupAction = 1;
export const knMoveUnitGroupAction = 2;
export const knAttackUnitGroupAction = 3;
export const knGuardUnitGroupAction = 4;
export const knMineUnitGroupAction = 5;
export const knGuardVicinityUnitGroupAction = 6;

export interface UnitListEntry {
  ut: number;
  gid: number;
  bf: number;
}

export abstract class UnitGroupAction {
  Init(_psz: string): boolean {
    return true;
  }
  // UnitGroupMgr.cpp:765-775 — base actions have no state
  LoadState(_pstm: import('./sm/stream.ts').Stream): boolean {
    return true;
  }
  SaveState(_pstm: import('./sm/stream.ts').Stream): boolean {
    return true;
  }
  abstract Perform(ctx: SimContext, pug: UnitGroup): boolean;
  Reset(): void {}
}

//
// UnitGroup (UnitGroupMgr.cpp:184-686)
//

export class UnitGroup {
  m_pactn: UnitGroupAction[] = [];
  m_pactnLast: UnitGroupAction | null = null;
  m_wf = kfUgNotRecentlyActivated;
  m_aule: UnitListEntry[] = [];
  m_pplr: Player | null = null;
  m_wfMunt = 0;
  m_nSpawnArea = -1;
  // BUG-COMPAT (sim-quirks A13): the C++ leaves m_nHealth uninitialized when the 'Health' prop is absent; the editor
  // always writes it, so every shipping group has one (default here is defensive)
  m_nHealth = 100;

  GetFlags(): number {
    return this.m_wf;
  }
  SetFlags(wf: number): void {
    this.m_wf = wf & 0xffff;
  }
  GetUnitCount(): number {
    return this.m_aule.length;
  }
  GetUnitList(): UnitListEntry[] {
    return this.m_aule;
  }
  GetOwner(): Player {
    return this.m_pplr!;
  }

  // UnitGroupMgr.cpp:224-260 (knVerUnitGroupState 5)
  LoadState(ctx: SimContext, pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 5) return false;
    const nAction = (pstm.ReadByte() << 24) >> 24;
    this.m_pactnLast = nAction !== -1 ? this.m_pactn[nAction] : null;
    this.m_wf = pstm.ReadWord();
    const cule = pstm.ReadWord();
    if (cule !== this.m_aule.length) {
      this.m_aule = new Array(cule);
      for (let i = 0; i < cule; i++) this.m_aule[i] = { ut: 0, gid: kgidNull, bf: 0 };
    }
    for (const pule of this.m_aule) {
      pule.ut = (pstm.ReadWord() << 16) >> 16;
      pule.gid = pstm.ReadWord();
      pule.bf = pstm.ReadByte();
    }
    for (const pactn of this.m_pactn) pactn.LoadState(pstm);
    this.m_wfMunt = pstm.ReadWord();
    return pstm.IsSuccess();
  }

  // UnitGroupMgr.cpp:262-305
  SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(5);
    if (this.m_pactnLast === null) pstm.WriteByte(0xff);
    else pstm.WriteByte(this.m_pactn.indexOf(this.m_pactnLast));
    pstm.WriteWord(this.m_wf);
    pstm.WriteWord(this.m_aule.length);
    for (const pule of this.m_aule) {
      pstm.WriteWord(pule.ut);
      pstm.WriteWord(pule.gid);
      pstm.WriteByte(pule.bf);
    }
    for (const pactn of this.m_pactn) pactn.SaveState(pstm);
    pstm.WriteWord(this.m_wfMunt);
    return pstm.IsSuccess();
  }

  // UnitGroupMgr.cpp:307-446 — walk the "UnitGroup <iug>" section's props in file order
  Init(ctx: SimContext, pini: IniReader, iug: number): boolean {
    const sec = pini.GetSection(`UnitGroup ${iug}`);
    if (sec === null) return true; // C++ FindNextProperty just finds nothing
    for (const prop of sec.props) {
      switch (prop.key[0]) {
        case 'S': {
          const out = { n: 0 };
          ParseNumber({ psz: prop.value }, out);
          if (prop.key[1] === 'i') {
            // Side -> the owning Player
            this.m_pplr = ctx.plrm.GetPlayer(out.n);
          } else if (prop.key[1] === 'p') {
            if (prop.key === 'SpawnArea') this.m_nSpawnArea = out.n;
            else this.m_wf |= out.n !== 0 ? kfUgSpawn : 0;
          }
          break;
        }
        case 'L': {
          // LoopForever
          const out = { n: 0 };
          ParseNumber({ psz: prop.value }, out);
          if (out.n !== 0) this.m_wf |= kfUgLoopForever;
          break;
        }
        case 'R': {
          // RandomGroup or ReplaceGroup
          const out = { n: 0 };
          ParseNumber({ psz: prop.value }, out);
          if (prop.key[1] === 'a') {
            if (out.n !== 0) this.m_wf |= kfUgRandomGroup;
          } else if (prop.key[1] === 'e') {
            if (out.n !== 0) this.m_wf |= kfUgReplaceGroup;
          }
          break;
        }
        case 'C': {
          // CreateAtLevelLoad
          const out = { n: 0 };
          ParseNumber({ psz: prop.value }, out);
          if (out.n !== 0) this.m_wf |= kfUgCreateAtLevelLoad;
          break;
        }
        case 'U': {
          // Units: "<total>,<ut>,<count>[,<ut>,<count>...]"
          const pp = { psz: prop.value };
          const out = { n: 0 };
          if (!ParseNumber(pp, out)) return false;
          const cule = out.n;
          this.m_aule = [];
          const outUt = { n: 0 };
          while (ParseNumber(pp, outUt)) {
            const nUnitType = outUt.n;
            const outC = { n: 0 };
            if (!ParseNumber(pp, outC)) return false;
            for (let i = 0; i < outC.n; i++) {
              this.m_aule.push({ ut: nUnitType, gid: kgidNull, bf: 0 });
            }
          }
          if (this.m_aule.length !== cule) return false; // C++ asserts
          break;
        }
        case 'A': {
          if (prop.key[1] === 'g') {
            // Aggressiveness
            const out = { n: 0 };
            ParseNumber({ psz: prop.value }, out);
            this.m_wfMunt = AggBitsFromAgg(out.n);
          } else {
            // Action
            if (!this.LoadAction(prop.value)) return false;
          }
          break;
        }
        case 'H': {
          // Health (percent of armor strength)
          const out = { n: 0 };
          ParseNumber({ psz: prop.value }, out);
          this.m_nHealth = out.n;
          break;
        }
        case 'N': // Name (DEBUG only)
          break;
        default:
          return false; // C++ asserts
      }
    }
    return true;
  }

  // UnitGroupMgr.cpp:692-737 — the LoadAction switch
  LoadAction(pszValue: string): boolean {
    const pp = { psz: pszValue };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    const nAction = out.n;
    const sz = pp.psz;

    let pactn: UnitGroupAction;
    switch (nAction) {
      case knWaitUnitGroupAction:
        pactn = new WaitUnitGroupAction();
        break;
      case knSetSwitchUnitGroupAction:
        pactn = new SetSwitchUnitGroupAction();
        break;
      case knMoveUnitGroupAction:
        pactn = new MoveUnitGroupAction();
        break;
      case knAttackUnitGroupAction:
        pactn = new AttackUnitGroupAction();
        break;
      case knGuardUnitGroupAction:
        pactn = new GuardUnitGroupAction();
        break;
      case knGuardVicinityUnitGroupAction:
        pactn = new GuardVicinityUnitGroupAction();
        break;
      case knMineUnitGroupAction:
        pactn = new MineUnitGroupAction();
        break;
      default:
        throw new Error(`UnitGroup.LoadAction: unknown action type ${nAction} (C++ asserts)`);
    }

    if (!pactn.Init(sz)) return false;
    this.m_pactn.push(pactn); // link in last
    return true;
  }

  // UnitGroupMgr.cpp:448-495 — slot a freshly built unit into the list (the BuildMgr
  // calls this through builder.ts's UnitGroupLike seam)
  OnBuilt(punt: UnitGob): void {
    let fNeedsUnit = false;
    let ut = punt.GetUnitType();
    for (const pule of this.m_aule) {
      if (pule.bf & kfUleBuilt) continue;
      if (pule.ut !== ut) {
        fNeedsUnit = true;
        continue;
      }

      pule.gid = punt.GetId();
      pule.bf |= kfUleBuilt;
      if (punt.GetFlags() & kfGobMobileUnit) {
        const pmunt = punt as MobileUnitGob;
        let wf = (pmunt.GetMobileUnitFlags() & ~kfMuntAggressivenessBits) | this.m_wfMunt;
        // Human-controlled units don't attack while following move orders
        if (!(this.m_pplr!.GetFlags() & kfPlrComputer)) wf &= ~kfMuntAttackEnemiesWhenMoving;
        pmunt.SetMobileUnitFlags(wf);
      }
      punt.SetHealth(
        Math.trunc((this.m_nHealth * (punt.GetConsts() as UnitConsts).GetArmorStrength()) / 100),
      );

      // Keep scanning so fNeedsUnit gets determined, but stop matching
      ut = kutNone;
    }

    if (!fNeedsUnit) this.m_wf &= ~kfUgNeedsUnit;
  }

  // UnitGroupMgr.cpp:497-512 — dynamic add (Replicator clones)
  AddUnit(punt: UnitGob, fReplicant: boolean): void {
    this.m_aule.push({
      gid: punt.GetId(),
      ut: (punt.GetConsts() as UnitConsts).ut,
      bf: fReplicant ? kfUleBuilt | kfUleReplicant : kfUleBuilt,
    });
  }

  // UnitGroupMgr.cpp:514-531 — replicants are trailing entries; truncate at the first
  RemoveReplicants(): void {
    for (let iule = 0; iule < this.m_aule.length; iule++) {
      if (this.m_aule[iule].bf & kfUleReplicant) {
        this.m_aule.length = iule;
        return;
      }
    }
  }

  // UnitGroupMgr.cpp:533-602
  Activate(ctx: SimContext): void {
    this.m_wf =
      (this.m_wf | kfUgNeedsUnit | kfUgActive | kfUgActivatedBefore) &
      ~(kfUgWaitingToReplace | kfUgNotRecentlyActivated);
    this.m_pactnLast = null;

    // Human-controlled players don't auto-attack while following move orders
    let wfMuntFilter: number;
    if (!(this.m_pplr!.GetFlags() & kfPlrComputer))
      wfMuntFilter = ~kfMuntAttackEnemiesWhenMoving & 0xffff;
    else wfMuntFilter = 0xffff;

    if (this.m_wf & kfUgSpawn) {
      // Spawn MobileUnits at the Area's center, Structures at its upper-left
      const trc = ctx.gobm.GetAreaRect(this.m_nSpawnArea);
      const ptx = Math.trunc((trc.left + trc.right) / 2);
      const pty = Math.trunc((trc.top + trc.bottom) / 2);

      for (const pule of this.m_aule) {
        const puntc = ctx.unitConsts[pule.ut] as UnitConsts;
        const fxHealth = Math.trunc((this.m_nHealth * puntc.GetArmorStrength()) / 100);
        let punt: UnitGob | null = null;
        if ((1 << pule.ut) & kumStructures) {
          if (!ctx.gobm.IsBelowLimit(knLimitStruct, this.m_pplr!)) continue;
          const pstruc = puntc as StructConsts;
          if (
            ctx.trmap!.IsOccupied(
              trc.left,
              trc.top,
              pstruc.ctxReserve,
              pstruc.ctyReserve,
              kbfStructure | kbfMobileUnit,
            )
          )
            continue;
          punt = CreateGob(ctx, pstruc.gt) as UnitGob | null;
          if (punt === null) continue;
          punt.Init(WcFromTc(trc.left), WcFromTc(trc.top), this.m_pplr!, fxHealth, 0, null);
        } else {
          if (!ctx.gobm.IsBelowLimit(knLimitMobileUnit, this.m_pplr!)) continue;
          punt = CreateGob(ctx, puntc.gt) as UnitGob | null;
          if (punt === null) continue;
          const wpt = FindNearestFreeTile(ctx, ptx, pty);
          punt.Init(wpt.wx, wpt.wy, this.m_pplr!, fxHealth, 0, null);
          const pmunt = punt as MobileUnitGob;
          pmunt.SetMobileUnitFlags(
            (pmunt.GetMobileUnitFlags() & ~kfMuntAggressivenessBits) |
              (this.m_wfMunt & wfMuntFilter),
          );
        }

        pule.gid = punt.GetId();
        pule.bf |= kfUleBuilt;
      }
      this.m_wf &= ~kfUgNeedsUnit;
    } else {
      const pbldm = ctx.sim!.GetBuildMgr()! as unknown as {
        BuildUnit(ut: number, pug: UnitGroup, nArea: number): boolean;
      };
      for (const pule of this.m_aule) {
        pule.gid = kgidNull;
        pule.bf &= ~kfUleBuilt;
        pbldm.BuildUnit(pule.ut, this, this.m_nSpawnArea);
      }
    }

    this.Update(ctx);
  }

  // UnitGroupMgr.cpp:604-686
  Update(ctx: SimContext): void {
    // Clear out dead units; count miners even when inactive (probably in the processor)
    let fAllGone = true;
    for (const pule of this.m_aule) {
      if (pule.gid !== kgidNull) {
        if (ctx.gobm.GetGob(pule.gid, pule.ut !== kutGalaxMiner) !== null) fAllGone = false;
        else pule.gid = kgidNull;
      }
    }

    if (this.m_wf & kfUgNeedsUnit) return;

    if (fAllGone) {
      this.m_wf &= ~kfUgActive;
      for (const pactn of this.m_pactn) pactn.Reset();
      this.RemoveReplicants();
      if (this.m_wf & kfUgReplaceGroup) this.Activate(ctx);
      return;
    }

    if (this.m_wf & kfUgWaitingToReplace) return;

    // Run actions from the block point (or the start), looping if LoopForever
    let pactnNext = this.m_pactnLast;
    do {
      let i = pactnNext === null ? 0 : this.m_pactn.indexOf(pactnNext);
      while (i < this.m_pactn.length) {
        const pactn = this.m_pactn[i];
        if (!pactn.Perform(ctx, this)) {
          this.m_pactnLast = pactn;
          return;
        }
        this.m_pactnLast = null;
        i++;
      }
      pactnNext = null;
    } while (this.m_wf & kfUgLoopForever);

    // All actions done; ReplaceGroups stay for recreation
    if (this.m_wf & kfUgReplaceGroup) this.m_wf |= kfUgWaitingToReplace;
    else {
      this.RemoveReplicants();
      this.m_wf &= ~kfUgActive;
    }
  }
}

//
// UnitGroupMgr (UnitGroupMgr.cpp:9-181)
//

export class UnitGroupMgr {
  private ctx: SimContext;
  m_aug: UnitGroup[] = [];

  constructor(ctx: SimContext) {
    this.ctx = ctx;
  }

  // UnitGroupMgr.cpp:22-47 — count from [General] UnitGroupCount
  Init(pini: IniReader): boolean {
    const cug = pini.GetInt('General', 'UnitGroupCount');
    if (cug === null) return false;
    for (let iug = 0; iug < cug; iug++) {
      const ug = new UnitGroup();
      if (!ug.Init(this.ctx, pini, iug)) return false;
      this.m_aug.push(ug);
    }
    return true;
  }

  // UnitGroupMgr.cpp:49-71 (knVerUnitGroupMgrState 1)
  LoadState(pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    for (const ug of this.m_aug) ug.LoadState(this.ctx, pstm);
    return pstm.IsSuccess();
  }

  SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    for (const ug of this.m_aug) ug.SaveState(pstm);
    return pstm.IsSuccess();
  }

  // UnitGroupMgr.cpp:73-82
  ActivateUnitGroup(iug: number): void {
    if ((this.m_aug[iug].GetFlags() & kfUgActive) !== 0) return;
    this.m_aug[iug].Activate(this.ctx);
  }

  // UnitGroupMgr.cpp:84-137 — shuffle semantics: random groups don't repeat until all
  // have been activated; ONE sync-RNG draw when any are eligible
  ActivateRandomUnitGroup(): void {
    let cug = 0;
    let cugNotRecentlyActive = 0;
    for (const pug of this.m_aug) {
      const wf = pug.GetFlags();
      if ((wf & (kfUgActive | kfUgRandomGroup)) === kfUgRandomGroup) {
        if (wf & kfUgNotRecentlyActivated) cugNotRecentlyActive++;
        cug++;
      }
    }
    if (cug === 0) return;

    if (cugNotRecentlyActive === 0) {
      for (const pug of this.m_aug) {
        const wf = pug.GetFlags();
        if ((wf & (kfUgActive | kfUgRandomGroup)) === kfUgRandomGroup) {
          pug.SetFlags(wf | kfUgNotRecentlyActivated);
        }
      }
    } else {
      cug = cugNotRecentlyActive;
    }

    cug = this.ctx.rng.GetRandom() % cug; // SYNC RNG

    let iug = 0;
    for (const pug of this.m_aug) {
      if (
        (pug.GetFlags() & (kfUgActive | kfUgRandomGroup | kfUgNotRecentlyActivated)) ===
        (kfUgRandomGroup | kfUgNotRecentlyActivated)
      ) {
        if (iug === cug) {
          pug.Activate(this.ctx);
          return;
        }
        iug++;
      }
    }
  }

  // UnitGroupMgr.cpp:141-152
  Update(): void {
    for (const pug of this.m_aug) {
      if (pug.GetFlags() & kfUgActive) pug.Update(this.ctx);
    }
  }

  // UnitGroupMgr.cpp:154-161
  CreateAtLevelLoadGroups(): boolean {
    for (const pug of this.m_aug) {
      if (pug.GetFlags() & kfUgCreateAtLevelLoad) pug.Activate(this.ctx);
    }
    return true;
  }

  // UnitGroupMgr.cpp:163-181 — which active group owns this (built) gid?
  GetUnitGroup(gid: number): UnitGroup | null {
    for (const pug of this.m_aug) {
      if (!(pug.GetFlags() & kfUgActive)) continue;
      for (const pule of pug.GetUnitList()) {
        if (pule.gid === gid && pule.bf & kfUleBuilt) return pug;
      }
    }
    return null;
  }
}

//
// UnitGroupActions (UnitGroupMgr.cpp:761-1444)
//

// UnitGroupMgr.cpp:775-845
export class WaitUnitGroupAction extends UnitGroupAction {
  m_ctWait = 0;
  m_fWaiting = false;
  m_tStart = 0;

  // UnitGroupMgr.cpp:777-806 (knVerWaitUnitGroupActionState 1)
  override LoadState(pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_fWaiting = pstm.ReadByte() !== 0;
    this.m_tStart = pstm.ReadDword() | 0;
    return pstm.IsSuccess();
  }

  override SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_fWaiting ? 1 : 0);
    pstm.WriteDword(this.m_tStart);
    return pstm.IsSuccess();
  }

  override Init(psz: string): boolean {
    const out = { n: 0 };
    if (!ParseNumber({ psz }, out)) return false;
    this.m_ctWait = out.n * 100;
    return true;
  }

  override Reset(): void {
    this.m_fWaiting = false;
  }

  override Perform(ctx: SimContext): boolean {
    const t = ctx.sim!.GetTickCount();
    if (this.m_fWaiting) {
      if (t - this.m_tStart >= this.m_ctWait) {
        this.m_fWaiting = false;
        return true;
      }
      return false;
    }
    this.m_fWaiting = true;
    this.m_tStart = t;
    return false;
  }
}

// UnitGroupMgr.cpp:856-873
export class SetSwitchUnitGroupAction extends UnitGroupAction {
  m_iSwitch = 0;
  m_fOn = false;

  override Init(psz: string): boolean {
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseNumber(pp, out)) return false;
    this.m_iSwitch = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_fOn = out.n === 1;
    return true;
  }

  override Perform(ctx: SimContext): boolean {
    (
      ctx.sim!.GetLevel()! as unknown as {
        GetTriggerMgr(): { SetSwitch(i: number, f: boolean): void };
      }
    )
      .GetTriggerMgr()
      .SetSwitch(this.m_iSwitch, this.m_fOn);
    return true;
  }
}

// UnitGroupMgr.cpp:875-1009 — send everyone to the area center, then wait until all
// living members are idle
export class MoveUnitGroupAction extends UnitGroupAction {
  m_nArea = 0;
  m_fWaiting = false;

  // UnitGroupMgr.cpp:890-911 (knVerMoveUnitGroupActionState 1)
  override LoadState(pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_fWaiting = pstm.ReadByte() !== 0;
    return pstm.IsSuccess();
  }

  override SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_fWaiting ? 1 : 0);
    return pstm.IsSuccess();
  }

  override Init(psz: string): boolean {
    this.Reset();
    const out = { n: 0 };
    if (!ParseArea({ psz }, out)) return false;
    this.m_nArea = out.n;
    return true;
  }

  override Reset(): void {
    this.m_fWaiting = false;
  }

  override Perform(ctx: SimContext, pug: UnitGroup): boolean {
    if (!this.m_fWaiting) {
      const trc = ctx.gobm.GetAreaRect(this.m_nArea, pug.GetOwner().GetSide());

      // min group speed
      let wcMoveDistPerUpdate = 0x7fffffff; // kwcMax
      for (const pule of pug.GetUnitList()) {
        const punt = ctx.gobm.GetGob(pule.gid) as MobileUnitGob | null;
        if (punt === null) continue;
        const pmuntc = punt.GetConsts() as MobileUnitConsts;
        if (pmuntc.GetMoveDistPerUpdate() < wcMoveDistPerUpdate) {
          wcMoveDistPerUpdate = pmuntc.GetMoveDistPerUpdate();
        }
      }

      const tcRadius = RadiusFromUnitCount(pug.GetUnitCount()) + 1;
      const xCenter = Math.trunc((trc.left + trc.right) / 2);
      const yCenter = Math.trunc((trc.top + trc.bottom) / 2);
      for (const pule of pug.GetUnitList()) {
        const punt = ctx.gobm.GetGob(pule.gid) as MobileUnitGob | null;
        if (punt === null) continue;
        SendMoveAction(
          ctx,
          punt.GetId(),
          WcFromTc(xCenter),
          WcFromTc(yCenter),
          tcRadius,
          wcMoveDistPerUpdate,
        );
      }

      this.m_fWaiting = true;
      return false;
    }

    this.m_fWaiting = false;
    for (const pule of pug.GetUnitList()) {
      const punt = ctx.gobm.GetGob(pule.gid) as UnitGob | null;
      if (punt === null) continue;
      if (!punt.IsIdle()) {
        this.m_fWaiting = true;
        break;
      }
    }
    return !this.m_fWaiting;
  }
}

// UnitGroupMgr.cpp:1011-1205 — group-centroid nearest-target selection, retarget on
// kill, complete when <seconds> elapse
export class AttackUnitGroupAction extends UnitGroupAction {
  m_um = 0;
  m_wfCaSideMask = 0;
  m_ctWait = 0;
  m_fWaiting = false;
  m_tStart = 0;
  m_gidTarget = kgidNull;

  // UnitGroupMgr.cpp:1014-1040 (knVerAttackUnitGroupActionState 1)
  override LoadState(pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_fWaiting = pstm.ReadByte() !== 0;
    this.m_tStart = pstm.ReadDword() | 0;
    this.m_gidTarget = pstm.ReadWord();
    return pstm.IsSuccess();
  }

  override SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_fWaiting ? 1 : 0);
    pstm.WriteDword(this.m_tStart);
    pstm.WriteWord(this.m_gidTarget);
    return pstm.IsSuccess();
  }

  override Init(psz: string): boolean {
    this.Reset();
    const pp = { psz };
    const out = { n: 0 };
    if (!ParseUnitMask(pp, out)) return false;
    this.m_um = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_wfCaSideMask = out.n;
    if (!ParseNumber(pp, out)) return false;
    this.m_ctWait = out.n * 100;
    return true;
  }

  override Reset(): void {
    this.m_fWaiting = false;
  }

  override Perform(ctx: SimContext, pug: UnitGroup): boolean {
    const t = ctx.sim!.GetTickCount();
    if (this.m_fWaiting) {
      if (t - this.m_tStart >= this.m_ctWait) {
        this.m_fWaiting = false;
        return true;
      }
    } else {
      this.m_fWaiting = true;
      this.m_tStart = t;
      this.m_gidTarget = kgidNull;
    }

    const sidmTarget = GetSideMaskFromCaSideMask(
      ctx,
      pug.GetOwner().GetSide(),
      this.m_wfCaSideMask,
    );
    let puntTarget = ctx.gobm.GetGob(this.m_gidTarget) as UnitGob | null;
    if (puntTarget !== null) {
      // the target may have been taken over to a side we aren't attacking
      if (!(GetSideMask(puntTarget.GetSide()) & sidmTarget)) puntTarget = null;
    }
    if (puntTarget !== null) return false; // everyone's already attacking it

    this.m_gidTarget = kgidNull;

    // group centroid
    let txGroupCenter = 0;
    let tyGroupCenter = 0;
    let cpunt = 0;
    for (const pule of pug.GetUnitList()) {
      const punt = ctx.gobm.GetGob(pule.gid) as UnitGob | null;
      if (punt === null) continue;
      const tpt = { tx: 0, ty: 0 };
      punt.GetTilePosition(tpt);
      txGroupCenter += tpt.tx;
      tyGroupCenter += tpt.ty;
      cpunt++;
    }
    txGroupCenter = Math.trunc(txGroupCenter / cpunt);
    tyGroupCenter = Math.trunc(tyGroupCenter / cpunt);

    // nearest matching enemy by squared tile distance (gob-list order breaks ties)
    let nDist = 0x7fff;
    for (let pgob = ctx.gobm.GetFirstGob(); pgob !== null; pgob = ctx.gobm.GetNextGob(pgob)) {
      const ff = pgob.GetFlags();
      if ((ff & (kfGobUnit | kfGobActive)) !== (kfGobUnit | kfGobActive) >>> 0) continue;
      const punt = pgob as UnitGob;
      if (!((punt.GetConsts() as UnitConsts).um & this.m_um)) continue;
      if (!(GetSideMask(punt.GetSide()) & sidmTarget)) continue;

      const tpt = { tx: 0, ty: 0 };
      punt.GetTilePosition(tpt);
      const dtx = tpt.tx - txGroupCenter;
      const dty = tpt.ty - tyGroupCenter;
      const nDistT = dtx * dtx + dty * dty;
      if (nDistT < nDist) {
        this.m_gidTarget = punt.GetId();
        nDist = nDistT;
      }
    }

    if (this.m_gidTarget === kgidNull) return false; // no valid targets; keep waiting

    const target = ctx.gobm.GetGob(this.m_gidTarget) as UnitGob;
    const wpt = { wx: 0, wy: 0 };
    target.GetAttackPoint(wpt);

    for (const pule of pug.GetUnitList()) {
      const pmunt = ctx.gobm.GetGob(pule.gid) as MobileUnitGob | null;
      if (pmunt === null) continue;
      const msgT = newMessage(kmidAttackAction, ksmidNull, pmunt.GetId());
      msgT.AttackCommand.wptTarget.wx = wpt.wx;
      msgT.AttackCommand.wptTarget.wy = wpt.wy;
      msgT.AttackCommand.gidTarget = this.m_gidTarget;
      msgT.AttackCommand.wptTargetCenter.wx = wpt.wx;
      msgT.AttackCommand.wptTargetCenter.wy = wpt.wy;
      msgT.AttackCommand.tcTargetRadius = 0;
      msgT.AttackCommand.wcMoveDistPerUpdate = 0;
      ctx.smm!.SendMsg(msgT);
    }

    return false; // keep waiting until the timer runs out
  }
}

// UnitGroupMgr.cpp:1207-1308 — guard in place for <seconds>
export class GuardUnitGroupAction extends UnitGroupAction {
  m_ctWait = 0;
  m_fWaiting = false;
  m_tStart = 0;

  // UnitGroupMgr.cpp:1213-1240 / 1356-1383 (knVer*UnitGroupActionState 1)
  override LoadState(pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_fWaiting = pstm.ReadByte() !== 0;
    this.m_tStart = pstm.ReadDword() | 0;
    return pstm.IsSuccess();
  }

  override SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_fWaiting ? 1 : 0);
    pstm.WriteDword(this.m_tStart);
    return pstm.IsSuccess();
  }

  override Init(psz: string): boolean {
    this.Reset();
    const out = { n: 0 };
    if (!ParseNumber({ psz }, out)) return false;
    this.m_ctWait = out.n * 100;
    return true;
  }

  override Reset(): void {
    this.m_fWaiting = false;
  }

  override Perform(ctx: SimContext, pug: UnitGroup): boolean {
    const t = ctx.sim!.GetTickCount();
    if (this.m_fWaiting) {
      if (t - this.m_tStart >= this.m_ctWait) {
        this.m_fWaiting = false;
        return true;
      }
    } else {
      this.m_fWaiting = true;
      this.m_tStart = t;
      for (const pule of pug.GetUnitList()) {
        const pmunt = ctx.gobm.GetGob(pule.gid) as MobileUnitGob | null;
        if (pmunt === null) continue;
        ctx.smm!.SendMsg(newMessage(kmidGuardAction, ksmidNull, pmunt.GetId()));
      }
    }
    return false;
  }
}

// UnitGroupMgr.cpp:1322-1347 — send every miner mining; no waiting
export class MineUnitGroupAction extends UnitGroupAction {
  override Perform(ctx: SimContext, pug: UnitGroup): boolean {
    for (const pule of pug.GetUnitList()) {
      const pmnr = ctx.gobm.GetGob(pule.gid);
      if (pmnr === null) continue;
      if (pmnr.GetType() !== kgtGalaxMiner) continue;

      // SendMineCommand macro (ht.h:2106) — gidTarget null, target (kwxInvalid, kwxInvalid)
      const msgT = newMessage(kmidMineCommand, kgidNull, pmnr.GetId());
      msgT.MineCommand.wptTarget.wx = kwxInvalid;
      msgT.MineCommand.wptTarget.wy = kwxInvalid;
      msgT.MineCommand.gidTarget = kgidNull;
      ctx.smm!.SendMsg(msgT);
    }
    return true;
  }
}

// UnitGroupMgr.cpp:1356-1444 — guard-vicinity for <seconds>
export class GuardVicinityUnitGroupAction extends UnitGroupAction {
  m_ctWait = 0;
  m_fWaiting = false;
  m_tStart = 0;

  // UnitGroupMgr.cpp:1213-1240 / 1356-1383 (knVer*UnitGroupActionState 1)
  override LoadState(pstm: import('./sm/stream.ts').Stream): boolean {
    const nVer = pstm.ReadByte();
    if (nVer !== 1) return false;
    this.m_fWaiting = pstm.ReadByte() !== 0;
    this.m_tStart = pstm.ReadDword() | 0;
    return pstm.IsSuccess();
  }

  override SaveState(pstm: import('./sm/stream.ts').Stream): boolean {
    pstm.WriteByte(1);
    pstm.WriteByte(this.m_fWaiting ? 1 : 0);
    pstm.WriteDword(this.m_tStart);
    return pstm.IsSuccess();
  }

  override Init(psz: string): boolean {
    this.Reset();
    const out = { n: 0 };
    if (!ParseNumber({ psz }, out)) return false;
    this.m_ctWait = out.n * 100;
    return true;
  }

  override Reset(): void {
    this.m_fWaiting = false;
  }

  override Perform(ctx: SimContext, pug: UnitGroup): boolean {
    const t = ctx.sim!.GetTickCount();
    if (this.m_fWaiting) {
      if (t - this.m_tStart >= this.m_ctWait) {
        this.m_fWaiting = false;
        return true;
      }
    } else {
      this.m_fWaiting = true;
      this.m_tStart = t;
      for (const pule of pug.GetUnitList()) {
        const pmunt = ctx.gobm.GetGob(pule.gid) as MobileUnitGob | null;
        if (pmunt === null) continue;
        ctx.smm!.SendMsg(newMessage(kmidGuardVicinityAction, ksmidNull, pmunt.GetId()));
      }
    }
    return false;
  }
}
